import clsx from 'clsx'
import {orderBy} from 'lodash'
import React from 'react'
import {Redirect, Link, useHistory} from 'react-router-dom'
import {makeStyles} from 'external/material'
import Fade from '@mui/material/Fade'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import CloseIcon from '@mui/icons-material/Close'
import {faFileExport} from "@fortawesome/free-solid-svg-icons"
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome"

import Navigation from './Navigation'
import Timer from './Timer'
import Sections from './Sections'
import Section from './Section'
import Question from './Question'
import QuestionIndicators from './components/question-indicators'
import FullScreenDialog from '../../FullScreenDialog'

import create from 'helpers/command'

const useStyles = makeStyles(assessmentStyles)
 
export default function PreviewAssessment ({context, imported}) {
  const classes = useStyles()
  const history = useHistory()

  const {quizAssessment: assessment, quizSections, quizQuestions, assessment: assessmentId,  translate, sectionId, link, quizTypesMap, sectionName} = context
  const {title, type} = assessment

  const assessmentLoaded = !context.quizSearch.assessment

  const sections = assessment.id == assessmentId
    ? orderBy(quizSections.filter(s => quizQuestions[s.id] && quizQuestions[s.id].length > 0),'index').map(section => {
      const questions = orderBy(quizQuestions[section.id] || [], 'index')
      return {...section, questions}
    })
    : []

  const redirect = !imported && sections.length > 0 && !sectionId
  const redirectLink = redirect && link({sectionId: sections[0].id})

  const typeName = imported ? (context.languageId === 'es' ? 'Examen' : 'Exam') : translate(quizTypesMap[type])

  const isSection = !context.questionId

  const closeLink = imported ? imported.closeLink : link({}, context.quizPreviewFrom || '/:languageId/:role/:page/assessments', {query: context.quizPreviewFromQuery})

  async function handleTakeTest () {
    if (imported) return imported.onStart()
    const [id] = await create('quiz','startTestAssessment')({id: assessment.id})
    if (id) {
      history.push(context.link({action: 'take', sectionId: undefined, questionId: undefined}))
    }
  }

  async function handleSave() {
    const [id] = await create('quiz', 'duplicateAssessment')({id: assessment.id, title: assessment.title})
    context.navigate({tab: 'assessments'},'/:languageId/:role/:page/:tab', {query:{id}})
  }

  return redirectLink
    ? (<Redirect to={redirectLink} />)
    : (<FullScreenDialog TransitionComponent={undefined} open={true} >
        <Box className={classes.root}>
          <Box className={classes.header}>
            <Box className={classes.label}>{translate('preview-header')}</Box>
            {sections.length > 0 && (
              <Box className={clsx(classes.button, classes.take)} onClick={handleTakeTest}>
                <Box className="label">{translate('take-test-exam')} {typeName}</Box>
              </Box>
            )}
            {!imported && !assessment.owned && (
            <Box className={clsx(classes.button, classes.save)} onClick={handleSave}>
              <Box className="icon"><FontAwesomeIcon icon={faFileExport} /></Box>
              <Box className="label">{translate('save-assessment')}</Box>
            </Box>
            )}
            <Link className={clsx(classes.button, classes.close)} to={closeLink}>
              <Box className="icon"><CloseIcon /></Box>
              <Box className="label">{translate('close')}</Box>
            </Link>
          </Box>
          <Box className={classes.preview}>
            <Paper className={classes.view}>
              <Box className={classes.viewHeader}>
                <Box className="type">{typeName}:</Box>
                <Box className="title">{title}</Box>
                {!isSection && <QuestionIndicators />}
              </Box>
              <Box className={classes.viewContent}>
                {isSection && (<Section {...{sections, context}} />)}
                {!isSection && (<Question {...{sections, context}} />)} 
              </Box>
              <Box className={classes.viewNavigation}>
                <Navigation {...{sections, context}} />
              </Box>
            </Paper>
            <Box className={classes.side}>
              {assessment.isAssignment && <Timer context={context} />}
              <Sections {...{sections, context}} />
            </Box>
          </Box>
        </Box>
      </FullScreenDialog>)
}

export function assessmentStyles (theme) {
  return {
    root: {
      display: 'flex',
      flexDirection: 'column',
      flexGrow: 1,
      overflowY: 'hidden'
    },
    header: {
      padding: 10,
      display: 'flex',
    },
    label: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      flexGrow: 1,
      color: '#4d5361',
      fontSize:'13pt',
      fontWeight: 'bold'
    },
    button: {
      textDecoration: 'none',
      color: '#fff',
      display: 'flex',
      padding: '7px 15px 7px 12px',
      backgroundColor:'#2ca5ec',
      borderRadius: 3,
      '& > div': {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
      },
      '& .label': {
        fontSize: '13pt'
      },
      cursor: 'pointer',
      '&:hover': {
      },
    },
    save: {
      marginLeft: 20,
      '& .icon': {
        marginTop: -3,
        marginRight: 3,
        fontSize: '12pt'
      },
    },
    close: {
      marginLeft: 20,
      '& .icon': {
        marginTop: -3,
        marginRight: 3,
        fontSize: '8pt'
      },
    },
    preview: {
      display: 'flex',
      padding: 10,
      flexGrow: 1,
      overflowY: 'hidden'
    },
    view: {
      flexGrow: 1,
      overflowY: 'hidden',
      display: 'flex',
      flexDirection: 'column',
    },
    viewHeader: {
      display: 'flex',
      color: '#4d5361',
      fontSize:'13pt',
      padding: 20,
      borderBottom: 'solid 1px #d3d8db',
      "& .type": {
        fontWeight: 'bold'
      },
      "& .title": {
        marginLeft: 3,
        flexGrow: 1
      },
    },
    viewContent: {
      display: 'flex',
      flexGrow: 1,
      overflowY: 'hidden',
    },
    viewNavigation: {
      borderTop: 'solid 1px #d3d8db',
      padding: 20
    },
    side: {
      marginLeft: 25,
      display: 'flex',
      flexDirection: 'column',
      minWidth: 255,
      maxWidth: 255
    },
  }
}
